import { IFactory } from "../../factories";
import { IFramework } from "../../frameworks";
import { IUser } from "../../users";
import {
    createApiQueryHook,
    IApiQueryFunction,
    IOrderable,
    IPaginateable,
    mapOrder,
    mapPagination,
} from "../../utils/api";
import { mapRelationalFilter, RelationalFilter } from "../../utils/filters";
import { serialize } from "../../utils/url";
import { IClassroomUser } from "../types";
import { IClassroomList } from "../types/IClassroomList";

export interface IClassroomListQueryParameters
    extends
        IPaginateable,
        IOrderable<"title" | "locationTitle" | "startDate" | "endDate" | "nbWorks" | "lastDateActivityOnWorks"> {
    filters?: {
        title?: string;
        description?: string;
        locationTitle?: string;
        externalReferences?: string;
        frameworks?: RelationalFilter<IFramework>;
        factory?: RelationalFilter<IFactory>;
        learners?: RelationalFilter<IUser | IClassroomUser>;
        trainers?: RelationalFilter<IUser>;
        restricted?: boolean;
        startDateBefore?: string;
        startDateStrictlyBefore?: string;
        startDateAfter?: string;
        startDateStrictlyAfter?: string;
        endDateBefore?: string;
        endDateStrictlyBefore?: string;
        endDateAfter?: string;
        endDateStrictlyAfter?: string;
        groups?: string[];
        forceActive?: boolean;
    };
}

export type IClassroomListQueryResult = IClassroomList;

const mapFilters = (filters: IClassroomListQueryParameters["filters"]) => {
    const {
        title,
        description,
        locationTitle,
        externalReferences,
        frameworks,
        factory,
        learners,
        trainers,
        restricted,
        startDateBefore,
        startDateStrictlyBefore,
        startDateAfter,
        startDateStrictlyAfter,
        endDateBefore,
        endDateStrictlyBefore,
        endDateAfter,
        endDateStrictlyAfter,
        groups,
        forceActive,
    } = filters ?? {};

    return {
        title,
        description,
        externalReferences,
        restricted,
        forceActive,
        groups,
        "location.title": locationTitle,
        "frameworks.uuid": mapRelationalFilter(frameworks),
        "factory.uuid": mapRelationalFilter(factory),
        "learners.uuid": mapRelationalFilter(learners),
        "trainers.uuid": mapRelationalFilter(trainers),
        "startDate[before]": startDateBefore,
        "startDate[strictly_before]": startDateStrictlyBefore,
        "startDate[after]": startDateAfter,
        "startDate[strictly_after]": startDateStrictlyAfter,
        "endDate[before]": endDateBefore,
        "endDate[strictly_before]": endDateStrictlyBefore,
        "endDate[after]": endDateAfter,
        "endDate[strictly_after]": endDateStrictlyAfter,
    };
};

export const classroomListQuery: IApiQueryFunction<IClassroomListQueryParameters> = ({
    filters,
    order,
    pagination,
}) => {
    return {
        method: "GET",
        url: `/classrooms${serialize(
            { ...mapPagination(pagination), ...mapOrder(order), ...mapFilters(filters) },
            true,
        )}`,
    };
};

export const useClassroomListQuery = createApiQueryHook<IClassroomListQueryParameters, IClassroomListQueryResult, any>(
    classroomListQuery,
);
